<template>
  <div class="row">
    <div class="col-12">
      <div class="card">
        <!-- 헤더 -->
        <DetailHeader :headerTitle="headerTitle" :backTo="'Vehicles'"/>

        <!-- 바디 -->
        <div class="pt-3 pb-4 d-flex justify-content-center">
          <div v-if="isEdit && loading">
            <TableBodyLoader :minHeight="578" :offsetY="-50" message="상세정보를 불러오는 중..." />
          </div>
          <div v-else class="w-100 px-5">

            <div class="row gx-4">
              <div class="col-lg-8 col-md-7">
                <section class="card border-0" data-tour="dcreate.left">
                  <div class="card-body">
                    <h6 class="mb-3">상세정보</h6>
                    <!-- 썸네일 -->
                    <ImagePicker
                      class="mb-3"
                      v-model:file="form.imgFile"
                      :existing-preview="form.imgPreview"
                      :existing-name="form.imgFilename"
                      :max-size="10 * 1024 * 1024"
                      label="대표 이미지"
                      alt="scanplace"
                      @selected="onImgSelected"
                      @cleared="onImgCleared"
                      @error="toastError"
                    />

                  <!-- 필드 -->
                  <div class="row g-3">
                    <div class="col-md-6">
                      <label class="form-label">차량번호 <span class="text-danger">*</span></label>
                      <input
                        type="text"
                        class="form-control"
                        v-model.trim="form.vehicleNo"
                        placeholder="예: 12가3456 / ABC-1234"
                      />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">차량 종류</label>
                      <input
                        type="text"
                        class="form-control"
                        v-model.trim="form.vehicleType"
                        placeholder="예: 트레일러, 탑차, 냉동탑차 등"
                      />
                    </div>

                    <div class="col-md-6">
                      <label class="form-label">연료 종류 <span class="text-danger">*</span></label>
                      <select class="form-select" v-model="form.fuelType" data-tour="vehicle.fuelType">
                        <option :value="''" disabled>연료를 선택하세요</option>
                        <option value="DIESEL">DIESEL (경유)</option>
                        <option value="GASOLINE">GASOLINE (휘발유)</option>
                        <option value="LPG">LPG (액화석유가스)</option>
                      </select>
                    </div>

                  <!-- 메타 -->
                  <div class="row g-3 mt-1 mb-3" v-if="isEdit">
                    <div class="col-md-6">
                      <label class="form-label">생성일시</label>
                      <input type="text" class="form-control" :value="formatDate(form.createdAt) || '-'" disabled />
                    </div>
                    <div class="col-md-6">
                      <label class="form-label">수정일시</label>
                      <input type="text" class="form-control" :value="formatDate(form.updatedAt) || '-'" disabled />
                    </div>
                  </div>
                </div>
              </div>
            </section>
            
              <fuel-efficiency-slider
                data-tour="dcreate.fuel"
                class="mt-3"
                v-model="form.fuelEfficiency"
                :fuelType="form.fuelType"
                :min="form.fuelType === 'ELECTRIC' ? 5 : 1"
                :max="form.fuelType === 'ELECTRIC' ? 40 : 30"
                :step="0.1"
                :title="form.fuelType === 'ELECTRIC' ? '에너지 소비량' : '연비 (km/L)'"
                :hint="form.fuelType === 'ELECTRIC' 
                  ? '100km 주행 시 평균 전력 사용량입니다.' 
                  : '차량의 평균 연비를 조절하세요.'"
              />
           </div>

            <!-- ▼▼ 오른쪽: 탄소배출량 슬라이더 ▼▼ -->
            <div class="col-lg-4 col-md-5 mt-3 mt-md-0 d-flex flex-column" data-tour="dcreate.right">
              <co2-slider
                v-model="emissionLocal"
                :min="emissionMin"
                :max="emissionMax"
                :step="emissionStep"
                title="탄소배출량"
                hint="드래그로 탄소 배출량을 조절하세요."
                :autoExpandMax="true"
                :expandUnit="50"
              />
              <co2-info kind="vehicle"/>
            </div>

            </div> <!-- /row -->

                <!-- 액션 -->
                <div class="d-flex justify-content-between mt-5 mb-3 gap-2 px-2">
                  <!-- 왼쪽: 목록가기 -->
                  <soft-button 
                    color="outline-secondary" 
                    @click="$router.push({ name: 'Vehicles', query: { ...$route.query } })"
                  >
                    목록
                  </soft-button>
                  <!-- 오른쪽: 삭제/저장 -->
                  <div class="d-flex gap-2">
                    <soft-button v-if="isEdit" color="danger" @click="remove" :disabled="loading">삭제</soft-button>
                    <soft-button color="success" @click="save" :disabled="loading" data-tour="dcreate.save">저장</soft-button>
                  </div>
                </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <VehicleTour />
<SoftConfirm ref="confirm" />
</template>

<script>
import SoftConfirm from "@/components/SoftConfirm.vue";
import DetailHeader from "@/components/DetailHeader.vue";
import SoftButton from "@/components/SoftButton.vue";
import TableBodyLoader from "@/components/loading/TableBodyLoader.vue";
import ImagePicker from "@/views/components/common/ImagePicker.vue";
import Co2Info from "@/views/components/co2/Co2Info.vue";
import Co2Slider from "@/views/components/co2/Co2Slider.vue";
import FuelEfficiencySlider from "@/views/components/vehicles/FuelEfficiencySlider.vue";
import VehicleTour from "@/components/tour/VehicleTour.vue";
import api from "@/api/api";

export default {
  name: "VehicleDetail",
  components: { SoftConfirm, DetailHeader, SoftButton
    , TableBodyLoader, ImagePicker, Co2Info, Co2Slider
    , FuelEfficiencySlider, VehicleTour },
  inject: ['showLoading', 'hideLoading'],
  props: {
    mode: { type: String, default: "create" },
    id: { type: [String, Number], default: "" }
  },
  data() {
    return {
      loading: false,
      form: {
        id: null,
        vehicleNo: "",
        carrierId: null,
        vehicleType: "",
        fuelType: "",
        fuelEfficiency: 0,
        emissionPerKm: 0, 

        mfId: "",
        atchFileId: "",

        imgFile: null,
        imgPreview: "",
        imgFilename: "",
        createdAt: "",
        updatedAt: ""
      },
      imgChanged: false,
      imgRemoved: false, 
        
      emissionMin: 0,
      emissionMax: 3,
      emissionStep: 0.01,
      emissionLocal: 0,
      
      EMISSION_FACTORS: {
        DIESEL: 2.68,
        GASOLINE: 2.31,
        LPG: 1.51,
      },
    };
  },
  computed: {
    isEdit() { return this.mode === "edit" && !!this.id; },
    headerTitle() { return this.isEdit ? "운송수단 수정" : "운송수단 등록"; },
    displayFileName() {
      return this.form.imgFile?.name || this.form.imgFilename || "";
    },
  },
  watch: {
    'form.emissionPerKm'(val) {
      const num = Number(val);
      if (!Number.isNaN(num)) {
        const fixed = Number(num.toFixed(2));
        if (fixed !== this.emissionLocal) this.emissionLocal = fixed;
        if (fixed > this.emissionMax) this.emissionMax = Math.ceil(fixed / 50) * 50;
      }
    },
    emissionLocal(val) {
      const fixedStr = Number(val).toFixed(4);
      if (fixedStr !== String(this.form.emissionPerKm)) this.form.emissionPerKm = fixedStr;
    },
    'form.fuelEfficiency'(v) {
      this.recalcEmissionFromEfficiency();
    },
    'form.fuelType'(v) {
      this.recalcEmissionFromEfficiency();
    },
    'form.emissionPerKm'(val) {
      const num = Number(val);
      if (!Number.isNaN(num)) {
        const fixed = Number(num.toFixed(4));
        if (fixed !== this.emissionLocal) this.emissionLocal = fixed;
        if (fixed > this.emissionMax) this.emissionMax = Math.ceil(fixed / 50) * 50;
      }
    },
    emissionLocal(val) {
      const fixedStr = Number(val).toFixed(4);
      if (fixedStr !== String(this.form.emissionPerKm)) this.form.emissionPerKm = fixedStr;
    },
  },
  async mounted() {
    this.form.mfId = this.$store.getters['UserStore/getMfId'];
    if (this.isEdit) await this.fetchDetail();

    const v = Number(this.form.emissionPerKm || 0);
    this.emissionLocal = Number.isFinite(v) ? Number(v.toFixed(4)) : 0;
    if (this.emissionLocal > this.emissionMax) {
      this.emissionMax = Math.ceil(this.emissionLocal / 50) * 50;
    }

    this._tourImgHandler = (e) => {
      const { file, blob, name } = e.detail || {};
      if (!file || !blob) return;

      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      const previewUrl = URL.createObjectURL(blob);

      this.form.imgFile     = file;
      this.form.imgPreview  = previewUrl;
      this.form.imgFilename = name || file.name || 'demo.jpg';

      this.imgChanged = true;
      this.imgRemoved = false;
    };
    window.addEventListener('tour:setImage', this._tourImgHandler);
  },
  beforeUnmount() {
    if (this.form.imgPreview) URL.revokeObjectURL(this.form.imgPreview);
    window.removeEventListener('tour:setImage', this._tourImgHandler);
    if (this.form.imgPreview?.startsWith('blob:')) URL.revokeObjectURL(this.form.imgPreview);
  },
  methods: {
    async fetchDetail() {
      this.loading = true;
      try {
        const [res] = await Promise.all([
          api.get(`/vehicle`, { params: { id: this.id } }),
          new Promise(r => setTimeout(r, 300))
        ]);
        const item = res.data?.result?.vehicle || {};
        const files = res.data?.result?.files || [];
        Object.assign(this.form, item);
        
        if (files.length > 0) {
          this.form.imgPreview  = files[0].downloadUrl || "";
          this.form.imgFilename = files[0].originalName || "";
        } else {
          this.form.imgPreview  = "";
          this.form.imgFilename = "";
        }
        this.files = files;

        const v = Number(this.form.emissionPerKm || 0);
        this.emissionLocal = Number.isFinite(v) ? Number(v.toFixed(4)) : 0;
        if (this.emissionLocal > this.emissionMax) {
          this.emissionMax = Math.ceil(this.emissionLocal / 50) * 50;
        }
      } catch (e) {
        this.toastError?.("상세 조회 중 오류가 발생했습니다.");
      } finally {
        this.loading = false;
      }
    },
    onImgSelected({ file, previewUrl, name }) {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = file;
      this.form.imgPreview  = previewUrl;
      this.form.imgFilename = name;

      this.imgChanged = true;
      this.imgRemoved = false;
    },
    onImgCleared() {
      if (this.form.imgPreview?.startsWith('blob:')) {
        URL.revokeObjectURL(this.form.imgPreview);
      }
      this.form.imgFile     = null;
      this.form.imgPreview  = "";
      this.form.imgFilename = "";

      this.imgChanged = false;
      this.imgRemoved = true;
    },
    buildFormData() {
      const fd = new FormData();
      fd.append("vehicleNo", this.form.vehicleNo ?? "");
      if (this.form.carrierId !== null && this.form.carrierId !== undefined) fd.append("carrierId", String(this.form.carrierId));
      if (this.form.vehicleType)   fd.append("vehicleType", this.form.vehicleType);
      if (this.form.fuelType)      fd.append("fuelType", this.form.fuelType);
      if (this.form.fuelEfficiency !== "" && this.form.fuelEfficiency !== null && this.form.fuelEfficiency !== undefined)
        fd.append("fuelEfficiency", Number(this.form.fuelEfficiency).toFixed(2));
      if (this.form.emissionPerKm !== "" && this.form.emissionPerKm !== null && this.form.emissionPerKm !== undefined)
        fd.append("emissionPerKm", Number(this.form.emissionPerKm).toFixed(4));

      if (this.form.mfId !== null && this.form.mfId !== undefined) fd.append("mfId", String(this.form.mfId));
      if (this.form.atchFileId) fd.append("atchFileId", this.form.atchFileId);

      if (this.form.imgFile) fd.append("file", this.form.imgFile);

      return fd;
    },
    async save() {
      if (!this.form.vehicleNo?.trim()) { this.toastError?.("차량번호를 입력하세요."); return; }

      const action = this.isEdit ? '수정' : '등록';
      const ok = await this.$refs.confirm.open({
        title: `운송수단 ${action}`,
        message: `${action} 하시겠습니까?`,
        confirmText: action,
        cancelText: "취소",
        confirmColor: "success",
      });
      if (!ok) return;

      const delay = (ms) => new Promise(r => setTimeout(r, ms));
      const started = performance.now();

      this.showLoading?.('저장중...');
      try {
        const fd = this.buildFormData();
        if (this.isEdit) {
          await api.put(`/vehicle/${this.id}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        } else {
          const { data } = await api.post(`/vehicle`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          this.form.id = data?.result?.id;
        }

        const elapsed = performance.now() - started;
        if (elapsed < 1000) await delay(1000 - elapsed);

        this.toastSuccess?.(`운송수단이 ${action}되었습니다.`);
        this.$router.push({
          name: "VehicleEdit",
          params: { id: this.form.id },
          query: { ...this.$route.query, t: Date.now()},
        });

      } catch (e) {
        console.log(e)
        this.toastError?.(`${action} 중 오류가 발생했습니다.`);
      } finally {
        this.hideLoading?.();
      }
    },

    async remove() {
      if (!this.isEdit) return;

      const ok = await this.$refs.confirm.open({
        title: "삭제 확인",
        message: "정말 삭제하시겠습니까?",
        confirmText: "삭제",
        cancelText: "취소",
        confirmColor: "danger",
      });
      if (!ok) return;
  
      this.showLoading?.('삭제중...');
      try {
        const delay = ms => new Promise(r => setTimeout(r, ms));
        await Promise.all([
          api.delete(`/vehicle/${this.id}`),
          delay(500),
        ]);

        this.toastSuccess?.("삭제되었습니다.");
        this.$router.push({
          name: "Vehicles",
          query: { ...this.$route.query }, 
        });
      } catch (e) {
        this.toastError?.("삭제 중 오류가 발생했습니다.");
      } finally {
        this.hideLoading?.();
      }
    },
    computeEmissionPerKm(fuelType, efficiency) {
      const eff = Number(efficiency);
      if (!Number.isFinite(eff) || eff <= 0) return 0;

      if (fuelType === 'DIESEL') {
        return (1 / eff) * this.EMISSION_FACTORS.DIESEL;
      }
      if (fuelType === 'GASOLINE') {
        return (1 / eff) * this.EMISSION_FACTORS.GASOLINE;
      }
      if (fuelType === 'LPG') {
        return (1 / eff) * this.EMISSION_FACTORS.LPG;
      }
      return 0;
    },
    // 계산 후 form & 슬라이더 동기화
    recalcEmissionFromEfficiency() {
      const val = this.computeEmissionPerKm(this.form.fuelType, this.form.fuelEfficiency);
      const fixed = Number(val.toFixed(4));
      this.form.emissionPerKm = fixed;
      this.emissionLocal = fixed;               // 오른쪽 CO₂ 슬라이더도 즉시 반영
      if (fixed > this.emissionMax) {
        this.emissionMax = Math.ceil(fixed / 50) * 50;
      }
    },
  }
};
</script>

<style scoped>
.card { border: 1px solid rgba(0,0,0,.06) !important; }

</style>
